import React, { useEffect, useState } from 'react';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { translate } from '../services/translate';
import { platformSettingsService } from '../services/central-api/platform-settings';

interface LocationDropdownProps {
    label?: string;
    labelFontSize?: number;
    labelStyle?: StyleProp<TextStyle>;
    address: string;
    onAddressChange: React.Dispatch<React.SetStateAction<string>>;
    onInputChange?: React.Dispatch<React.SetStateAction<string>>;
    onPinChange?: (pos: LatLng | undefined) => void;
    viewStyle?: StyleProp<ViewStyle>;
    disabled?: boolean;
    required?: boolean;
}

export default function LocationSelector({ label, labelFontSize, labelStyle, address, onAddressChange, onInputChange, onPinChange, viewStyle, disabled = false, required }: LocationDropdownProps) {
    const { styles, theme } = useStyles(stylesheet);

    const [placeId, setPlaceId] = useState<string>();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { data } = platformSettingsService.useMapCentralPoint();

    const geocoder = new google.maps.Geocoder();

    useEffect(() => {
        const fetchLatLng = async () => {
            if (onPinChange) {
                let data;

                if (placeId != '') {
                    data = await geocoder.geocode({ placeId: placeId });
                } else if (address) {
                    data = await geocoder.geocode({ address: address });
                }

                if (data) {
                    setPlaceId('');
                    onPinChange({ lat: data.results[0].geometry.location.lat(), lng: data.results[0].geometry.location.lng() });
                } else {
                    onPinChange(undefined);
                }
            }
        };

        if (!isMenuOpen) {
            fetchLatLng();
        }
    }, [isMenuOpen]);

    return (
        <View style={[viewStyle]}>
            {label &&
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    {<MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16)}>{label}</MyAppText>}
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
            }
            <GooglePlacesAutocomplete
                apiOptions={{
                    language: 'pt-BR',
                    region: 'BR',
                }}
                autocompletionRequest={{
                    // default value refers to the city of sao paulo
                    location: {
                        lat: data?.latitude ?? -23.533773,
                        lng: data?.longitude ?? -46.62529,
                    },
                    radius: data?.radius ?? 35,
                    componentRestrictions: { country: 'br' },
                }}
                selectProps={{
                    onChange: (value) => {
                        if (value) {
                            setPlaceId(value.value.place_id);
                            onAddressChange(value.label);
                        } else {
                            onAddressChange('');
                        }
                        setIsMenuOpen(false);
                    },
                    onInputChange: (value, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                            if (onInputChange) {
                                onInputChange(value);
                            }
                            onAddressChange(value);
                        }
                    },
                    onMenuOpen: () => setIsMenuOpen(true),
                    onMenuClose: () => setIsMenuOpen(false),
                    value: (!isMenuOpen && address) ? { label: address, value: {} } : null,
                    inputValue: isMenuOpen ? address : undefined,
                    menuIsOpen: isMenuOpen,
                    placeholder: translate('filter'),
                    loadingMessage: () => translate('loading'),
                    noOptionsMessage: () => translate('nothingToShow'),
                    isDisabled: disabled,
                    styles: {
                        option: (base, state) => ({
                            ...base,
                            color: state.isFocused
                                ? theme.colors.hoverText
                                : theme.colors.textColor,
                            backgroundColor: state.isFocused
                                ? theme.colors.primary['200']
                                : theme.colors.field.background,
                            cursor: 'pointer',
                            fontFamily: 'Open Sans',
                            transition: 'background-color 0.2s ease, color 0.2s ease',
                        }),
                        singleValue(base) {
                            base.color = theme.colors.textColor;
                            base.whiteSpace = 'break-spaces';
                            base.fontFamily = 'Open Sans';
                            return base;
                        },
                        placeholder(base) {
                            base.color = theme.colors.placeholder;
                            base.fontFamily = 'Open Sans';
                            return base;
                        },
                        input(base) {
                            base.cursor = disabled ? 'not-allowed' : 'text';
                            base.color = theme.colors.textColor;
                            base.fontFamily = 'Open Sans';
                            return base;
                        },
                        control(base) {
                            base.backgroundColor = theme.colors.field.background;
                            base.borderColor = theme.colors.field.border;
                            base.minHeight = 40;
                            base.fontFamily = 'Open Sans';
                            return base;
                        },
                        menuList(base) {
                            base.color = theme.colors.textColor;
                            base.backgroundColor = theme.colors.field.background;
                            base.fontFamily = 'Open Sans';
                            return base;
                        }
                    }
                }}
            />
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
}));
